import { useEffect, useState } from 'react';
import { useRouteLoaderData, useNavigate, useParams } from 'react-router';
import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import type { Language } from '@seek/melways-sites';
import { useMelwaysLink } from '@seek/melways-react';

import {
  PageLoader,
  type ReviewUpvotes,
  type Reviews,
  type SortOrder,
  useConfig,
} from '@seek/libs-shared';

import { useService, type FlagReviewPayload } from '../../../';
import { useAuth } from '../../../shared/providers/authProvider';
import { CompanyProfilesPage } from '../../../paths';
import { ReviewsPage } from './ReviewsPage';
import type { ReviewsTabModel } from './reviewsTabModel';
import { ReviewReplyNotice } from '../../components/reviewReplyNotice/ReviewReplyNotice';
import { SeoStructuredData } from '../../components/seoStructuredData/SeoStructuredData';
import { mapToSeoData } from '../../components/seoStructuredData/mapper';
import { isLocationBasedReviewRoute } from '../../../shared/utils/isLocationBasedReviewRoute';
import type { AppConfig } from '../../../shared/config';

export function ReviewsPageContainer({
  showReviewReplyNotice,
  trackWriteAReviewClicked,
  trackReviewsPaginationClicked,
  trackSortOrderChanged,
  trackSignInClicked,
  trackRegisterClicked,
}: {
  showReviewReplyNotice: boolean;
  trackWriteAReviewClicked: () => void;
  trackReviewsPaginationClicked: ({
    pageNumber,
  }: {
    pageNumber: number;
  }) => void;
  trackSortOrderChanged: ({ sortOrder }: { sortOrder: SortOrder }) => void;
  trackSignInClicked: () => void;
  trackRegisterClicked: () => void;
}) {
  const model =
    useRouteLoaderData<ReviewsTabModel>(CompanyProfilesPage.Reviews) ??
    useRouteLoaderData<ReviewsTabModel>(CompanyProfilesPage.ReviewsLocation);

  const { companyProfileService, locationService } = useService();
  const { authenticationStatus, ssoLogin } = useAuth();
  const [newModel, setNewModel] = useState<ReviewsTabModel | undefined>(model);
  const navigate = useNavigate();
  const localize = useMelwaysLink();
  const config = useConfig<AppConfig>();
  const params = useParams();
  const isLocationRoute = isLocationBasedReviewRoute({
    locationSlug: params.locationSlug,
    locationId: config.reviewLocation.id,
  });

  const getReviews = async ({
    companyId,
    page,
    sortOrder,
    perPage,
    locationId,
  }: {
    companyId: string;
    page: number;
    sortOrder: SortOrder;
    perPage?: number;
    locationId?: number;
  }): Promise<Reviews> => {
    const resp = await companyProfileService.getReviews({
      companyId,
      page,
      sortOrder,
      perPage,
      locationId,
    });
    return resp;
  };

  const upvoteReview = async ({
    reviewId,
    companyId,
  }: {
    reviewId: string;
    companyId: string;
  }): Promise<void> => {
    if (
      authenticationStatus === AUTHENTICATION_TYPES.UNAUTHENTICATED &&
      typeof ssoLogin === 'function'
    ) {
      ssoLogin();
    }
    await companyProfileService.postUpvoteReview({
      companyId,
      reviewId,
    });
  };

  const flagReview = async (
    companyId: string,
    reviewId: string,
    payload: FlagReviewPayload,
  ): Promise<boolean> => {
    if (
      authenticationStatus === AUTHENTICATION_TYPES.UNAUTHENTICATED &&
      typeof ssoLogin === 'function'
    ) {
      ssoLogin();
    }

    const response = await companyProfileService.postFlagReview({
      companyId,
      reviewId,
      details: payload.details,
      reason: payload.reason,
    });
    return response?.success || false;
  };

  const getLocationContextualName = (options: {
    locationId?: number;
    language: Language;
  }): string | undefined => locationService.getLocationContextualName(options);

  useEffect(() => {
    if (isLocationRoute && !model?.reviews?.reviews?.length) {
      /**
       ** Navigate to all-company-reviews page if there are no location-based reviews.
       */
      navigate(
        localize({
          language: config.language,
          path: `/companies/${params.companySlug}/reviews${window.location.search}${window.location.hash}`,
        }),
      );
      return;
    }

    const getUpvoteIds = async (companyId: string): Promise<ReviewUpvotes> => {
      let upvotedIds = {} as ReviewUpvotes;

      try {
        upvotedIds = await companyProfileService.getUpvoteIds({
          companyId,
        });
      } catch {
        /* empty */
      }
      return upvotedIds;
    };

    const loadUpvoteIds = async () => {
      const reviews = model?.reviews;
      if (
        authenticationStatus === AUTHENTICATION_TYPES.AUTHENTICATED &&
        reviews
      ) {
        const companyId = reviews.companyId;
        const upvotedIds = await getUpvoteIds(companyId);
        reviews.upvotedIds = upvotedIds;
        setNewModel({ ...model, reviews });
      }
    };
    loadUpvoteIds();
  }, [authenticationStatus, companyProfileService, model]);

  if (showReviewReplyNotice) {
    return <ReviewReplyNotice />;
  }

  return model || newModel ? (
    <>
      <SeoStructuredData
        disableIndexing={
          /**
           ** disable indexing for location-based reviews page if there are no reviews.
           */
          isLocationRoute && !model?.reviews?.reviews?.length
        }
        data={mapToSeoData(newModel || model)}
      />
      <ReviewsPage
        model={newModel || model}
        showReviewReplyNotice={showReviewReplyNotice}
        getReviews={getReviews}
        upvoteReview={upvoteReview}
        flagReview={flagReview}
        getLocationContextualName={getLocationContextualName}
        trackWriteAReviewClicked={trackWriteAReviewClicked}
        trackReviewsPaginationClicked={trackReviewsPaginationClicked}
        trackSortOrderChanged={trackSortOrderChanged}
        trackSignInClicked={trackSignInClicked}
        trackRegisterClicked={trackRegisterClicked}
      />
    </>
  ) : (
    <PageLoader />
  );
}
